import authHttp from "./authHttp";

// Admin endpoints live on the zion-auth service alongside /auth/login and
// /auth/session, because that is where the user records, the sessions, the
// login history and the audit trail are stored. They go through authHttp so
// the session cookie and the CSRF header are attached exactly like the login
// requests are.
const URL_USERS = "/auth/admin/users";
const URL_LOGIN_ATTEMPTS = "/auth/admin/login-attempts";
const URL_SESSIONS = "/auth/admin/sessions";
const URL_AUDIT = "/auth/admin/audit";
const URL_PERMISSIONS = "/auth/admin/permissions";
const URL_ALARM_RECIPIENTS = "/auth/admin/alarm-recipients";
const URL_PROFILE = "/auth/profile";

export const OUTCOMES = {
  all: "all",
  success: "success",
  failure: "failure",
};

function userPath(userId) {
  return URL_USERS + "/" + encodeURIComponent(userId);
}

/* ------------------------------------------------------------------ users */

export async function fetchUsers() {
  const response = await authHttp.get(URL_USERS);
  return response.data?.users ?? [];
}

export async function createUser({
  name,
  email,
  role,
  password,
  forcePasswordChange,
}) {
  const response = await authHttp.post(URL_USERS, {
    name,
    email,
    role,
    password,
    forcePasswordChange,
  });
  return response.data?.user ?? null;
}

// Partial update: only send the keys that actually changed, so toggling the
// enabled flag never rewrites the role and vice versa.
export async function updateUser(userId, changes) {
  const response = await authHttp.patch(userPath(userId), changes);
  return response.data?.user ?? null;
}

export function setUserEnabled(userId, enabled) {
  return updateUser(userId, { enabled });
}

export async function changeUserPassword(userId, password, forceChange) {
  const response = await authHttp.post(userPath(userId) + "/password", {
    password,
    forceChange,
  });
  return response.data ?? null;
}

// Clears a rate-limit lockout early instead of waiting for it to expire.
export async function unlockUser(userId) {
  const response = await authHttp.post(userPath(userId) + "/unlock");
  return response.data?.user ?? null;
}

// Removes the user's enrolled authenticator so they can enrol a new one.
export async function resetUserMfa(userId) {
  const response = await authHttp.post(userPath(userId) + "/mfa/reset");
  return response.data?.user ?? null;
}

export async function deleteUser(userId) {
  await authHttp.delete(userPath(userId));
}

/* --------------------------------------------------------- login attempts */

// The attempts table grows without bound, so the window is narrowed on the
// server (outcome, date range, username, limit) and only that window is
// paged in the browser, the same way the device tables in this app work.
export async function fetchLoginAttempts({
  outcome = OUTCOMES.all,
  username = null,
  from = null,
  to = null,
  limit = 500,
} = {}) {
  const params = { limit };
  if (outcome && outcome !== OUTCOMES.all) params.outcome = outcome;
  if (username) params.username = username;
  if (from) params.from = from;
  if (to) params.to = to;
  const response = await authHttp.get(URL_LOGIN_ATTEMPTS, { params });
  return response.data?.attempts ?? [];
}

/* ---------------------------------------------------------------sessions */

export async function fetchSessions() {
  const response = await authHttp.get(URL_SESSIONS);
  return response.data?.sessions ?? [];
}

export async function revokeSession(sessionId) {
  await authHttp.delete(URL_SESSIONS + "/" + encodeURIComponent(sessionId));
}

export async function revokeUserSessions(userId) {
  await authHttp.delete(URL_SESSIONS, { params: { userId } });
}

/* ------------------------------------------------------------------ audit */

export async function fetchAuditEntries({
  actor = null,
  action = null,
  from = null,
  to = null,
  limit = 500,
} = {}) {
  const params = { limit };
  if (actor) params.actor = actor;
  if (action) params.action = action;
  if (from) params.from = from;
  if (to) params.to = to;
  const response = await authHttp.get(URL_AUDIT, { params });
  return response.data?.entries ?? [];
}

/* ------------------------------------------------------------ permissions */

export async function fetchPermissions() {
  const response = await authHttp.get(URL_PERMISSIONS);
  return response.data?.roles ?? null;
}

export async function savePermissions(roles) {
  const response = await authHttp.put(URL_PERMISSIONS, { roles });
  return response.data?.roles ?? null;
}

/* -------------------------------------------------------- alarm recipients */

export async function fetchAlarmRecipients() {
  const response = await authHttp.get(URL_ALARM_RECIPIENTS);
  return response.data?.recipients ?? [];
}

export async function saveAlarmRecipients(recipients) {
  const response = await authHttp.put(URL_ALARM_RECIPIENTS, { recipients });
  return response.data?.recipients ?? [];
}

/* ---------------------------------------------------------- own profile */

// Self-service: available to every signed-in user, not just admins.
export async function changeOwnPassword({ currentPassword, newPassword }) {
  await authHttp.post(URL_PROFILE + "/password", {
    currentPassword,
    newPassword,
  });
}

export async function fetchOwnLoginAttempts({ limit = 20 } = {}) {
  const response = await authHttp.get(URL_PROFILE + "/login-attempts", {
    params: { limit },
  });
  return response.data?.attempts ?? [];
}

export async function fetchOwnSessions() {
  const response = await authHttp.get(URL_PROFILE + "/sessions");
  return response.data?.sessions ?? [];
}

export async function revokeOwnSession(sessionId) {
  await authHttp.delete(
    URL_PROFILE + "/sessions/" + encodeURIComponent(sessionId)
  );
}

/* ------------------------------------------------------------------ errors */

// Turns an axios failure into a translation key the panels can hand to the
// toast, so a 403 from a non-admin session does not read as a generic error.
export function getErrorKey(error) {
  const status = error?.response?.status;
  if (status === 403) return "admin-common.forbidden";
  if (status === 409) return "admin-common.email-taken";
  if (status === 422) {
    // The service distinguishes a bad current password from bad data so the
    // profile form can say which one the user got wrong.
    const code = error?.response?.data?.code;
    if (code === "invalid_current_password")
      return "profile.wrong-current-password";
    return "admin-common.invalid-data";
  }
  return "_common.error-request";
}
